// Insert mutator is broken in final-form-arrays from version v1.2.0
// https://github.com/final-form/final-form-arrays/issues/44
import React, {
  useCallback,
  useEffect,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { Field } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';
import { flatten } from 'lodash';

import {
  IntlConsumer,
  withStripes,
  stripesConnect,
} from '@folio/stripes/core';
import {
  Accordion,
  Col,
  Row,
  TextField,
  TextArea,
  Select,
} from '@folio/stripes/components';

import {
  ICONS,
  PRIORITY,
  UPDATE_STATUS,
  TASK_TEMPLATES_INITIAL_VALUES,
  TASK_TEMPLATES_FIELDS,
  JOB_TEMPLATE_FIELDS,
  NAME_LIMIT_MAX,
  DESCRIPTION_LIMIT_MAX,
  LOAD_LIMIT,
  LOAD_LIMIT_MAX,
} from '../../../../../constants';
import {
  composeValidators,
  validateFieldLength,
  validateRequiredField,
} from '../../../../../utils';
import { useCallout } from '../../../../../hooks';

import {
  ActionButton,
  ActionButtonWithTooltip,
  Assignment,
} from '../../../../common';
import {
  TaskTemplateTitle,
} from './components';

import css from './task-template.css';

const taskTemplatesPriority = ({ formatMessage }) => [
  {
    value: PRIORITY.LOW,
    label: formatMessage({ id: `ui-task-list.priority.${PRIORITY.LOW}` }),
  },
  {
    value: PRIORITY.MEDIUM,
    label: formatMessage({ id: `ui-task-list.priority.${PRIORITY.MEDIUM}` }),
  },
  {
    value: PRIORITY.HIGH,
    label: formatMessage({ id: `ui-task-list.priority.${PRIORITY.HIGH}` }),
  },
];

const validateNameLength = value => validateFieldLength(value, NAME_LIMIT_MAX);
const validateDescriptionLength = value => validateFieldLength(value, DESCRIPTION_LIMIT_MAX);

const moveUpCurrentByIndex = (index, swapElementsByIndex) => {
  const onClickHandler = () => swapElementsByIndex(index, index - 1);

  return (
    <span
      data-test-job-template-task-template-row-action-button-up
    >
      <ActionButton
        icon={ICONS.ARROW_UP}
        onClickHandler={onClickHandler}
      />
    </span>
  );
};

const moveDownCurrentByIndex = (index, swapElementsByIndex) => {
  const onClickHandler = () => swapElementsByIndex(index, index + 1);

  return (
    <span
      data-test-job-template-task-template-row-action-button-down
    >
      <ActionButton
        icon={ICONS.ARROW_DOWN}
        onClickHandler={onClickHandler}
      />
    </span>
  );
};

const addAfterCurrentByIndex = (index, addAfterIndexMutator) => {
  const onClickHandler = () => {
    addAfterIndexMutator(index + 1, TASK_TEMPLATES_INITIAL_VALUES);
  };

  return (
    <span
      data-test-job-template-task-template-row-action-button-add
    >
      <ActionButtonWithTooltip
        icon={ICONS.ADD}
        index={index}
        onClickHandler={onClickHandler}
      />
    </span>
  );
};

const deleteCurrentByIndex = (fields, index) => {
  const onClickHandler = () => {
    if (fields.value[index][TASK_TEMPLATES_FIELDS.UPDATE_STATUS] === UPDATE_STATUS.ADD) {
      fields.remove(index);
    } else {
      fields.update(index, {
        ...fields.value[index],
        [TASK_TEMPLATES_FIELDS.UPDATE_STATUS]: UPDATE_STATUS.DELETE,
      });
    }
  };

  return (
    <span
      data-test-job-template-task-template-row-action-button-delete
    >
      <ActionButtonWithTooltip
        icon={ICONS.DELETE}
        index={index}
        onClickHandler={onClickHandler}
      />
    </span>
  );
};

const TaskTemplate = ({
  form,
  mutator,
}) => {
  const {
    addAfterIndex,
    swapElementsByIndex,
  } = form.mutators;

  const showCallout = useCallout();

  const [teams, setTeams] = useState([]);
  const [teamsLoaded, setTeamsLoaded] = useState(false);
  const [users, setUsers] = useState({});

  const getTeams = useCallback(() => {
    setTeamsLoaded(false);

    mutator.teams.GET({
      params: {
        limit: LOAD_LIMIT_MAX,
      },
    })
      .then((response) => {
        setTeams(response.teams);
        setTeamsLoaded(true);
      })
      .catch(() => {
        setTeams([]);

        showCallout({
          type: 'error',
          message: <FormattedMessage id="ui-task-list.validation.communicationProblem" />,
        });
      });
  }, []);

  const getUsers = (value) => {
    const selectedTeamObj = teams.find((team) => team.id === value);
    const userIds = selectedTeamObj?.members
      ? [...selectedTeamObj.members]
      : [];
    const usersPromises = [];

    while (userIds.length) {
      usersPromises.push(mutator.users.GET({
        params: {
          limit: LOAD_LIMIT,
          query: userIds.splice(0, LOAD_LIMIT)
            .map(userId => `id==${userId}`)
            .join(' or '),
        }
      }));
    }

    return Promise.all(usersPromises)
      .then((userArrays) => {
        const combinedUsers = flatten(userArrays.map((arr) => arr.users));

        setUsers((state) => ({
          ...state,
          [value]: combinedUsers,
        }));
      })
      .catch(() => {
        setUsers((state) => ({
          ...state,
          [value]: [],
        }));

        showCallout({
          type: 'error',
          message: <FormattedMessage id="ui-task-list.validation.communicationProblem" />,
        });
      });
  };

  useEffect(
    () => {
      getTeams();
    },
    [],
  );

  const validate = (values) => {
    const errorArray = [];

    if (values) {
      values.forEach((value) => {
        const name = value[TASK_TEMPLATES_FIELDS.NAME];
        const description = value[TASK_TEMPLATES_FIELDS.DESCRIPTION];
        const updateStatus = value[TASK_TEMPLATES_FIELDS.UPDATE_STATUS];
        const errors = {};

        if (updateStatus !== UPDATE_STATUS.DELETE) {
          errors[TASK_TEMPLATES_FIELDS.NAME] = composeValidators(
            validateRequiredField,
            validateNameLength,
          )(name);
          errors[TASK_TEMPLATES_FIELDS.DESCRIPTION] = validateDescriptionLength(description);
        }

        errorArray.push(errors);
      });
    }

    return errorArray;
  };

  const isRowVisible = (index, fields) => {
    return fields.value[index][TASK_TEMPLATES_FIELDS.UPDATE_STATUS] !== UPDATE_STATUS.DELETE;
  };

  const getRowStyle = (index, fields) => {
    return {
      display: isRowVisible(index, fields) ? 'flex' : 'none'
    };
  };

  const isMoveUpButtonVisible = (index, fields) => {
    const firstVisibleIndex = fields.value.findIndex((task) => {
      return task[TASK_TEMPLATES_FIELDS.UPDATE_STATUS] !== UPDATE_STATUS.DELETE;
    });
    const isFirstVisibleIndex = index === firstVisibleIndex;

    return !isFirstVisibleIndex;
  };

  const isMoveDownButtonVisible = (index, fields) => {
    const lastVisibleIndex = fields.value.length - 1 - fields.value.slice().reverse().findIndex((task) => {
      return task[TASK_TEMPLATES_FIELDS.UPDATE_STATUS] !== UPDATE_STATUS.DELETE;
    });
    const isLastVisibleItem = index === lastVisibleIndex;

    return !isLastVisibleItem;
  };

  const isDeleteButtonVisible = (fields) => {
    const visibleFields = fields.value.filter((task) => {
      return task[TASK_TEMPLATES_FIELDS.UPDATE_STATUS] !== UPDATE_STATUS.DELETE;
    });

    return visibleFields.length !== 1;
  };

  return (
    <Accordion
      id="taskTemplate"
      label={<FormattedMessage id="ui-task-list.title.accordion.taskTemplates" />}
    >
      <IntlConsumer>
        {intl => (
          <>
            <TaskTemplateTitle />
            <FieldArray
              name={JOB_TEMPLATE_FIELDS.TASK_TEMPLATES}
              validate={validate}
            >
              {({ fields }) => fields.map((taskTemplate, index) => {
                return (
                  <Row
                    data-test-job-template-task-template-row
                    key={index}
                    className={css.taskTemplatesRow}
                    style={getRowStyle(index, fields)}
                  >
                    <Col
                      className={css.taskTemplatesCell}
                      sm={1}
                    >
                      <Row>
                        <Col sm={6}>
                          {isMoveUpButtonVisible(index, fields) && moveUpCurrentByIndex(index, swapElementsByIndex)}
                        </Col>
                        <Col sm={6}>
                          {isMoveDownButtonVisible(index, fields) && moveDownCurrentByIndex(index, swapElementsByIndex)}
                        </Col>
                      </Row>
                    </Col>

                    <Col
                      className={css.taskTemplatesBorderedCol}
                      sm={10}
                    >
                      <Row>
                        <Col
                          data-test-job-template-task-template-row-name-wrapper
                          sm={12}
                        >
                          <Field
                            data-test-job-template-task-template-row-name
                            required
                            component={TextField}
                            id={`${TASK_TEMPLATES_FIELDS.NAME}${index}`}
                            label={<FormattedMessage id="ui-task-list.jobTemplate.taskTemplates.field.name" />}
                            name={`${taskTemplate}.${TASK_TEMPLATES_FIELDS.NAME}`}
                            type="text"
                          />
                        </Col>
                      </Row>
                      <Row>
                        <Col sm={9}>
                          <Field
                            data-test-job-template-task-template-row-description
                            component={TextArea}
                            id={`${TASK_TEMPLATES_FIELDS.DESCRIPTION}${index}`}
                            label={<FormattedMessage id="ui-task-list.jobTemplate.taskTemplates.field.description" />}
                            name={`${taskTemplate}.${TASK_TEMPLATES_FIELDS.DESCRIPTION}`}
                            type="text"
                          />
                        </Col>
                        <Col
                          sm={3}
                        >
                          <Field
                            data-test-job-template-task-template-row-priority
                            component={Select}
                            dataOptions={taskTemplatesPriority(intl)}
                            id={`${TASK_TEMPLATES_FIELDS.PRIORITY}${index}`}
                            label={<FormattedMessage id="ui-task-list.jobTemplate.taskTemplates.field.priority" />}
                            name={`${taskTemplate}.${TASK_TEMPLATES_FIELDS.PRIORITY}`}
                            type="text"
                          />
                        </Col>
                      </Row>

                      <Row>
                        <Col sm={12}>
                          <Assignment
                            fields={fields}
                            index={index}
                            task={taskTemplate}
                            teams={teams}
                            teamsLoaded={teamsLoaded}
                            users={users}
                            getTeams={getTeams}
                            getUsers={getUsers}
                          />
                        </Col>
                      </Row>
                    </Col>

                    <Col
                      className={css.taskTemplatesCell}
                      sm={1}
                    >
                      <Row>
                        <Col sm={6}>
                          {addAfterCurrentByIndex(index, addAfterIndex)}
                        </Col>
                        <Col sm={6}>
                          {
                            isDeleteButtonVisible(fields) && (
                              deleteCurrentByIndex(fields, index)
                            )
                          }
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                );
              })}
            </FieldArray>
          </>
        )}
      </IntlConsumer>
    </Accordion>
  );
};

TaskTemplate.manifest = Object.freeze({
  teams: {
    type: 'okapi',
    path: 'tasks/teams',
    clientGeneratePk: false,
    throwErrors: false,
    accumulate: true,
    fetch: false
  },
  users: {
    type: 'okapi',
    path: 'users',
    clientGeneratePk: false,
    throwErrors: false,
    accumulate: true,
    fetch: false
  },
});

TaskTemplate.propTypes = {
  form: PropTypes.shape({
    mutators: PropTypes.shape({
      addAfterIndex: PropTypes.func.isRequired,
      swapElementsByIndex: PropTypes.func.isRequired,
    }).isRequired,
  }),
  mutator: PropTypes.shape({
    teams: PropTypes.shape({
      GET: PropTypes.func.isRequired
    }),
    users: PropTypes.shape({
      GET: PropTypes.func.isRequired
    }),
  }),
};

export default withStripes(stripesConnect(TaskTemplate));
